import { Result, err, ok } from 'neverthrow';
import { UrlRankRepository } from '../Repositories/UrlRankRepository';
import {
  UrlRankRequest,
  UrlRankResult,
  UrlRankError,
  URL_RANK_POLL_INTERVAL_MS,
  URL_RANK_POLL_MAX_MS,
  isUrlRankTerminalStatus,
} from '../../Model/UrlRank';

export type UrlRankProgressCallback = (progress: UrlRankResult) => void;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function extractApiMessage(error: any): string | undefined {
  const data = error?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data?.error && typeof data.error === 'string') return data.error;
  return data?.detail || data?.message;
}

export class LookupUrlRankUseCase {
  constructor(private repo: UrlRankRepository) {}

  async execute(
    request: UrlRankRequest,
    options?: {
      onProgress?: UrlRankProgressCallback;
      signal?: AbortSignal;
    },
  ): Promise<Result<UrlRankResult, UrlRankError>> {
    try {
      const validation = this.validateRequest(request);
      if (validation.isErr()) return err(validation.error);

      const payload: UrlRankRequest = {
        keyword: request.keyword.trim(),
        product_url: request.product_url.trim(),
        force_refresh: Boolean(request.force_refresh),
      };

      const outcome = await this.repo.createUrlRankJob(payload);

      if (outcome.kind === 'stored') {
        return ok({
          ...outcome.result,
          keyword: outcome.result.keyword ?? payload.keyword,
          product_url: outcome.result.product_url ?? payload.product_url,
        });
      }

      const { job, seed } = outcome;
      const progress: UrlRankResult = {
        ...seed,
        keyword: seed?.keyword ?? payload.keyword,
        product_url: seed?.product_url ?? payload.product_url,
        rank: seed?.rank ?? null,
        rank_check_status: job.rank_check_status,
        search_range: job.search_range ?? seed?.search_range,
        job_id: job.job_id,
        status_url: job.status_url,
      };
      options?.onProgress?.(progress);

      return await this.pollJob(
        job.status_url || job.job_id,
        progress,
        options,
      );
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return err({
          type: 'API_ERROR',
          message: '조회가 취소되었습니다.',
        });
      }

      return err({
        type: 'API_ERROR',
        message: '조회에 실패했습니다.',
        details:
          extractApiMessage(error) ||
          (error instanceof Error ? error.message : String(error)),
      });
    }
  }

  private async pollJob(
    jobRef: string,
    seed: UrlRankResult,
    options?: {
      onProgress?: UrlRankProgressCallback;
      signal?: AbortSignal;
    },
  ): Promise<Result<UrlRankResult, UrlRankError>> {
    const startedAt = Date.now();
    let latestProgress = seed;
    let firstTick = true;

    while (Date.now() - startedAt < URL_RANK_POLL_MAX_MS) {
      if (options?.signal?.aborted) {
        return err({
          type: 'API_ERROR',
          message: '조회가 취소되었습니다.',
        });
      }

      if (!firstTick) {
        await sleep(URL_RANK_POLL_INTERVAL_MS, options?.signal);
      }
      firstTick = false;

      try {
        const latest = await this.repo.getUrlRankJob(jobRef);
        const merged: UrlRankResult = {
          ...latestProgress,
          ...latest,
          keyword: latest.keyword ?? latestProgress.keyword,
          product_url: latest.product_url ?? latestProgress.product_url,
          job_id: latest.job_id ?? latestProgress.job_id,
          status_url: latest.status_url ?? latestProgress.status_url,
          search_range: latest.search_range ?? latestProgress.search_range,
          // API가 error: null 을 줄 수 있음 — 진행 중이면 무시
          error: latest.error || undefined,
        };
        latestProgress = merged;

        if (isUrlRankTerminalStatus(merged.rank_check_status)) {
          if (merged.rank_check_status === 'failed') {
            return err({
              type: 'API_ERROR',
              message: '조회에 실패했습니다.',
              details: merged.error || merged.message || undefined,
            });
          }
          return ok(merged);
        }

        options?.onProgress?.(merged);
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          return err({
            type: 'API_ERROR',
            message: '조회가 취소되었습니다.',
          });
        }
        // 일시 오류는 계속 재시도 (진행 UI 유지)
      }
    }

    // 시간 초과여도 실패 화면으로 덮지 않고, 마지막 진행 상태를 유지
    return ok({
      ...latestProgress,
      rank_check_status: latestProgress.rank_check_status || 'running',
      message: '아직 확인 중입니다. 잠시 후 다시 조회해주세요.',
    });
  }

  private validateRequest(
    request: UrlRankRequest,
  ): Result<void, UrlRankError> {
    if (!request.keyword?.trim() || !request.product_url?.trim()) {
      return err({
        type: 'VALIDATION_ERROR',
        message: '키워드와 상품 링크를 모두 입력해주세요.',
      });
    }

    try {
      new URL(request.product_url.trim());
    } catch {
      return err({
        type: 'VALIDATION_ERROR',
        message: '올바른 상품 링크를 입력해주세요.',
      });
    }

    return ok(undefined);
  }
}
